/* eslint-disable no-use-before-define */
import Link, { LinkProps } from "next/link"
import { useSearchParams } from "next/navigation"
import { PropsWithChildren, Suspense, forwardRef } from "react"
import { utmParamKeys } from "./utmParams"

interface LinkWithParamsProps extends LinkProps {
  className?: string
  style?: React.CSSProperties
  target?: React.HTMLAttributeAnchorTarget
}

// Exposed to the client via next.config `env`, so it is inlined at build time
// (deterministic across server/client — no hydration mismatch).
const SITE_ORIGIN = process.env.ORIGIN

// Same-origin absolute URLs are sometimes authored in the CMS
// (e.g. "https://site.co.il/#car-interest"). Left as-is they would be treated
// as external and trigger a full page reload; converting them to a relative
// path lets Next do client-side navigation (and in-page hash scrolling).
const toRelativeIfSameOrigin = (href: string): string => {
  if (!SITE_ORIGIN) return href
  try {
    const url = new URL(href)
    if (url.origin === new URL(SITE_ORIGIN).origin) {
      return `${url.pathname}${url.search}${url.hash}`
    }
  } catch {
    // Not an absolute URL (a relative href) — leave it unchanged.
  }
  return href
}

const LinkWithParams = forwardRef<HTMLAnchorElement, PropsWithChildren<LinkWithParamsProps>>(
  ({ href, children, style, target, ...props }, ref) => {
    const normalizedHref = typeof href === "string" ? toRelativeIfSameOrigin(href) : href
    return (
      <Suspense
        fallback={
          <Link ref={ref} style={style} href={normalizedHref} target={target} {...props}>
            {children}
          </Link>
        }
      >
        <LinkWithParamsInner
          ref={ref}
          href={normalizedHref}
          style={style}
          target={target}
          {...props}
        >
          {children}
        </LinkWithParamsInner>
      </Suspense>
    )
  },
)

LinkWithParams.displayName = "LinkWithParams"

const LinkWithParamsInner = forwardRef<HTMLAnchorElement, PropsWithChildren<LinkWithParamsProps>>(
  ({ href, children, style, target, ...props }, ref) => {
    const currentQuery = useSearchParams()
    if (typeof href === "string" && !href.startsWith("/")) {
      return (
        <Link ref={ref} style={style} href={href} target={target} {...props}>
          {children}
        </Link>
      )
    }

    let utmParams = Array.from(currentQuery.entries()).filter(([param]) =>
      utmParamKeys.some((key) => param.includes(key)),
    )

    // Fallback: extract UTM params from hash fragment (e.g., /#faq?utm_source=...)
    if (
      utmParams.length === 0 &&
      typeof window !== "undefined" &&
      window.location.hash.includes("?")
    ) {
      const hashQuery = window.location.hash.split("?").slice(1).join("?")
      const hashParams = new URLSearchParams(hashQuery)
      utmParams = Array.from(hashParams.entries()).filter(([param]) =>
        utmParamKeys.some((key) => param.includes(key)),
      )
    }

    const hrefParams =
      typeof href === "string" && href.includes("?")
        ? Array.from(new URL(href, "http://dummy.com").searchParams.entries())
        : []

    const allParams = [...utmParams, ...hrefParams]

    const { pathname, hash } =
      typeof href === "string" ? new URL(href, "http://dummy.com") : { pathname: "", hash: "" }

    // Build query string manually to ensure correct URL format: pathname?query#hash
    const queryString = allParams.map(([key, value]) => `${key}=${value}`).join("&")

    // Construct the final URL with proper ordering: pathname + query + hash
    // This ensures UTM params come BEFORE the hash fragment
    let finalHref = pathname || ""
    if (queryString) {
      finalHref += `?${queryString}`
    }
    if (hash) {
      finalHref += hash
    }

    return (
      <Link ref={ref} style={style} href={finalHref} target={target} {...props}>
        {children}
      </Link>
    )
  },
)

LinkWithParamsInner.displayName = "LinkWithParamsInner"

export default LinkWithParams
